import { faIcon } from "@utils/faIcon"

import { IconButton, Tooltip } from "@atoms"

import { Select } from "@molecules"

import useObjectsView from "@organisms/ObjectsView/ObjectsView.context"
import useTableView from "@organisms/ObjectsView/TableView/TableView.context"

import styles from "./SortingControl.module.scss"
import { SortingControlProps } from "./SortingControl.types"

const fallbackSortIcon = faIcon({ name: "arrow-up-arrow-down", style: "regular" })

export default function SortingControl(props: SortingControlProps) {
    const { isVisible } = props

    const { sortingEnabledColumns, activeSorting, activeSortingIcons, activeSortingLabels } = useTableView()
    const { setCurrentSortToAscending, setCurrentSortToDescending, onSortingItemSelection } = useObjectsView()

    const isDescending = !!activeSorting?.desc
    const directionIcon = isDescending ? activeSortingIcons?.desc : activeSortingIcons?.asc
    const directionLabel = isDescending ? activeSortingLabels?.desc : activeSortingLabels?.asc
    const toggleDirection = isDescending ? setCurrentSortToAscending : setCurrentSortToDescending

    return (
        <div className={styles.base}>
            <Select.Root>
                <Select.Input
                    isControlled={false}
                    placeholder="Select Column"
                    className={styles.columnSelect}
                    isTabbable={isVisible}
                    selectedItem={
                        activeSorting && sortingEnabledColumns
                            ? {
                                  value: activeSorting.id,
                                  label:
                                      (sortingEnabledColumns.find((c) => c.accessorKey === activeSorting.id)
                                          ?.header as string) ?? "",
                              }
                            : undefined
                    }
                    size="sm"
                />
                {sortingEnabledColumns && (
                    <Select.Viewport>
                        <Select.List title="Sort by" className={styles.columnSelect}>
                            <Select.ListGroup>
                                {sortingEnabledColumns.map((column) => {
                                    return (
                                        <Select.ListItem
                                            variant="default"
                                            key={column.accessorKey as string}
                                            item={{
                                                value: column.accessorKey as string,
                                                label: column.header as string,
                                            }}
                                            onSelect={() => onSortingItemSelection(column)}
                                            isSelected={activeSorting?.id === column.accessorKey}
                                        />
                                    )
                                })}
                            </Select.ListGroup>
                        </Select.List>
                    </Select.Viewport>
                )}
            </Select.Root>
            <Tooltip body={directionLabel} isDisabled={!directionLabel}>
                <IconButton
                    variant="subtle"
                    size="lg"
                    icon={directionIcon ?? fallbackSortIcon}
                    onClick={toggleDirection}
                    isDisabled={!activeSorting}
                    isTabbable={isVisible}
                    noDebounce={true}
                    aria-label={directionLabel ?? "Toggle sort direction"}
                />
            </Tooltip>
        </div>
    )
}
